import type { CmmdProjectTaskDraft, CmmdProjectTaskDraftCommit } from "@cmmd-forge/contracts";

import { CmmdApiError, cmmdApiClient } from "~/environments/cmmdApi";

export type CommitTaskDraftResult =
  | { readonly kind: "committed"; readonly draft: CmmdProjectTaskDraft }
  | { readonly kind: "failed"; readonly cause: unknown; readonly stale: boolean };

export async function commitTaskDraftWithReconciliation(input: {
  readonly projectId: string;
  readonly draftId: number;
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
}): Promise<CommitTaskDraftResult> {
  try {
    const result = await commitTaskDraft(input);
    return { kind: "committed", draft: result.draft };
  } catch (cause: unknown) {
    if (isConflict(cause)) return { kind: "failed", cause, stale: true };
    return reconcileAmbiguousCommit(input, cause);
  }
}

async function reconcileAmbiguousCommit(
  input: Parameters<typeof commitTaskDraftWithReconciliation>[0],
  originalCause: unknown,
): Promise<CommitTaskDraftResult> {
  try {
    const reconciled = await cmmdApiClient.getProjectTaskDraft(input.projectId, input.draftId);
    if (reconciled.status === "committed") {
      return { kind: "committed", draft: reconciled };
    }
    const replay = await commitTaskDraft(input);
    return { kind: "committed", draft: replay.draft };
  } catch (cause: unknown) {
    return isConflict(cause)
      ? { kind: "failed", cause, stale: true }
      : { kind: "failed", cause: originalCause, stale: false };
  }
}

function commitTaskDraft(
  input: Parameters<typeof commitTaskDraftWithReconciliation>[0],
): Promise<CmmdProjectTaskDraftCommit> {
  return cmmdApiClient.commitProjectTaskDraft(input.projectId, input.draftId, {
    expectedVersion: input.expectedVersion,
    idempotencyKey: input.idempotencyKey,
  });
}

function isConflict(cause: unknown): boolean {
  return cause instanceof CmmdApiError && cause.status === 409;
}
